/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useMemo, useState, useContext, useCallback, useImperativeHandle, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	MRT_ToggleFiltersButton,
	MRT_ToggleGlobalFilterButton,
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";
import moment from "moment";
import { Autocomplete, Box, TextField, Tooltip, Checkbox, Chip, IconButton, Collapse } from "@mui/material";
import * as _ from "lodash";
import debounce from "lodash/debounce";

import "./reports-page.scss";

import { Button, ICONS, Icon, GenericDropdown, Label } from "abs-ui-components";

import AbsModal from "../../../common/absModal/AbsModal";
import ImportReport from "../../../components/ImportReport/ImportReport";
import BreadCrumb from "../../../common/breadCrumb/BreadCrumb";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { REPORTS_TABLE_COLUMNS } from "./reports-page-columns";
import { accessAPIForResponse } from "../../../services/api-services/api-implementations";
import { CustomTableRow } from "../../../components/SignInModule/UserAutherization/utilities";
import Loader from "../../../common/Loader/Loader";
import { getDashboard } from "../../../services/DashboardService";
import AutocompleteDropdown from "../../../common/autocompleteDropdown/AutocompleteDropdown";
import { ClaimsContext } from "../../../services/ClaimsService";
import { ClaimType } from "../../../constants/claims";
import { FeatureType } from "../../../constants/FeatureToggles";
import { getYearOptions } from "../../../services/utilities";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import axiosHttp from "../../../services/interceptor";
import { InputAdornment } from "@mui/material";
import { SearchOff } from "@mui/icons-material";
import ColumnFilterTextField from "./ColumnFilterTextField";

const MultiSelectFilter = React.memo(({
	columnId,
	options,
	placeholder,
	columnWidth,
	getFilterValue,
	updateFilterValue,
}) => {

	return (
		<Autocomplete
			multiple
			options={options}
			disableCloseOnSelect
			value={getFilterValue(columnId) || []}
			isOptionEqualToValue={(option, value) => option === value}
			onChange={(e, newValue) => {
				updateFilterValue(columnId, newValue);
			}}
			size="small"

			renderOption={(props, option, { selected }) => (
				<li {...props} key={option}>
					<Checkbox checked={selected} />
					{option}
				</li>
			)}

			renderTags={(value, getTagProps) =>
				<Chip
					label={value.length > 1 ? `${value.length} ${columnId}s selected` : value[0]}
					size="small"
					sx={{
						backgroundColor: "gray",
						color: "white",

						"& .MuiChip-deleteIcon": {
							color: "white",
						},
					}}
				/>
			}

			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					placeholder={placeholder}
				/>
			)}

			sx={{
				width: columnWidth || "auto",

				"& .MuiInputBase-input": {
					color: "white",
				},

				"& .MuiSvgIcon-root": {
					color: "white",
				},

				"& .MuiInput-underline:before": {
					borderBottomColor: "white",
				},

				"& .MuiInput-underline:hover:not(.Mui-disabled):before": {
					borderBottomColor: "white",
				},

				"& .MuiInput-underline:after": {
					borderBottomColor: "white",
				},
			}}
		/>
	);
});


const HighlightedCell = ({
	value,
	columnId,
	reportListColumnFilters,
	user,
}) => {

	const currentFilter =
		reportListColumnFilters.find(
			(f) => f.id === columnId
		);

	const selectedValues =
		currentFilter?.value || [];

	const normalizedSelectedValues = selectedValues.map((item) => {

		if (item === "@Me") {
			return user?.email;
		}

		if (item === "@Unassigned") {
			return "";
		}

		return item;
	});

	const isMatched =
		Array.isArray(normalizedSelectedValues) &&
		normalizedSelectedValues.includes(value);

	return (
		<span
			style={{
				backgroundColor: isMatched
					? "rgb(255, 203, 127)"
					: "transparent",
				color: isMatched
					? "black"
					: "inherit",
				padding: "2px 4px",
				borderRadius: "4px",
			}}
		>
			{value}
		</span>
	);
};

const GlobalFilterTextField = React.forwardRef((props, ref) => {

	const {
		searchInput,
		applyGlobalSearch,
		autoFocus,
	} = props;

	const [value, setValue] = useState(searchInput ?? "");

	const inputRef = useRef(null);

	useEffect(() => {
		setValue(searchInput ?? "");
	}, [searchInput]);

	useImperativeHandle(ref, () => ({
		getValue: () => value,
		clear: () => setValue(""),
		focus: () => inputRef.current?.focus(),
	}));

	return (
		<TextField
			inputRef={inputRef}
			autoFocus={autoFocus}
			size="small"
			variant="outlined"
			placeholder="Type and Search"
			value={value}
			onChange={(e) => {
				const newValue = e.target.value;

				setValue(newValue);

				if (newValue === "" && searchInput) {
					applyGlobalSearch("");
				}
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					applyGlobalSearch(value);
				}
			}}
			InputProps={{
				startAdornment: (
					<InputAdornment position="start">
						<SearchIcon fontSize="small" />
					</InputAdornment>
				),
				endAdornment: value && (
					<InputAdornment position="end">
						<IconButton
							size="small"
							onClick={() => {
								setValue("");
								applyGlobalSearch("");
							}}
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
});


const ReportsPage = () => {

	const { userHasClaim, userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
	const user = useSelector((state) => state.userInfo.userData);
	const currentYear = moment().year();
	const [reportListRowSelection, setReportListRowSelection] = useState({});
	const [reportedData, setReportedData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [rowCount, setRowCount] = useState(0);
	const [reportListGlobalFilter, setReportListGlobalFilter] = useState("");
	const [pendingReportListGlobalFilter, setPendingReportListGlobalFilter] = useState("");
	const [reportListSort, setReportListSort] = useState([]);
	const [reportListColumnFilters, setReportListColumnFilters] = useState([]);
	const [pendingReportListColumnFilters, setPendingReportListColumnFilters] = useState([]);
	const [reportListPagination, setReportListPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});
	const [isImportReportModalOpen, setIsImportReportModalOpen] =
		useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	// const options = JSON.parse(localStorage.getItem("report-years"));
	const getReportingYear =
		localStorage.getItem("selectedYear") || currentYear.toString();
	const [selectedYearOption, setSelectedYearOption] = useState([
		getReportingYear,
	]);
	// const [selectAll, setSelectAll] = useState(false);
	const [actionBtnName, setActionBtnName] = useState("");
	const [showModel, setShowModel] = useState(false);
	const [engineers, setEngineers] = useState([]);
	const [selectedEngineer, setSelectedEngineer] = useState("");
	const [selectedReport, setselectedReport] = useState("");
	const [disableBtn, setDisableBtn] = useState(false);
	const [previousGlobalFilter, setPreviousGlobalFilter] = useState("");
	const [pageLoader, setPageLoader] = useState(false);
	const [options, setOptions] = useState([]);
	const [showWcnDropdown, setShowWcnDropdown] = useState(false);
	const [selectedWcn, setSelectedWcn] = useState();
	const [wcnOptionsList, setWcnOptionsList] = useState([]);
	const [exportReportEnabled, setExportReportEnabled] = useState(true);
	const [disableAssignBtn, setdisableAssignBtn] = useState(true);
	const reportTypes = useMemo(
		() => ["DCS", "EUMRV", "UKMRV", "FEU", "UKETS"],
		[]
	);

	const reportStatuses = useMemo(
		() => [
			"No Data Submitted",
			"Supporting Documents Needed",
			"Crosscheck In Progress",
			"Verification In Progress",
			"Verification Completed",
		],
		[]
	);
	const [engineersOptions, setEngineersOptions] = useState([]);
	const [imoVesselTypes, setImoVesselTypes] = useState([]);
	const [mrvVesselTypes, setMrvVesselTypes] = useState([]);
	const [searchInput, setSearchInput] = useState('');
	const [showSearch, setShowSearch] = useState(false);
	const globalSearchRef = useRef(null);
	const textFilterRefs = useRef({});
	const TEXT_FILTER_COLUMNS = [
    "ImoNumber",
    "VesselName",
    "WcnNumber",
    "CompanyName",
    "Flag",
];

	const [showbreadCrumb, setShowBreadCrumb] = useState(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		return storedValue !== null ? JSON.parse(storedValue) : true;
	});

	// Create a debounced setter function
	const debouncedSetHeaderFooterEnabled = debounce((value) => {
		setShowBreadCrumb(value);
	}, 100);

	 const isLegacyReportsEnabled =
		userFeaturesAreLoaded && userHasFeature(FeatureType.LegacyReports);

	useEffect(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		if (storedValue !== null) {
			debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
		}
	}, [debouncedSetHeaderFooterEnabled]);



	useEffect(() => {
		const loadOptions = async () => {
			const yearOptions = getYearOptions(isLegacyReportsEnabled);
			if (yearOptions.length > 0) {
				setOptions(yearOptions);
			} else {
				const fetchedDashboardData = await getDashboard();
				const years = fetchedDashboardData?.DashboardReportYears;
				const yearOptions = _.map(years, (x) => {
					return { label: x.ReportYear, value: x.ReportYear };
				});
				setOptions(yearOptions);
				localStorage.setItem(
					"report-years",
					JSON.stringify(yearOptions)
				);
			}
		};
		loadOptions();
	}, []);

	const handleSelect = (select) => {
		setSelectedEngineer(select?.value);
		setdisableAssignBtn(false);
	};

	const GetEngineersList = async (input) => {
		try {
			const response = await accessAPIForResponse(
				"GetReviewEngineers",
				null,
				[input]
			);
			if (response) {
				const engineers_options = response.map((obj, index) => ({
					label: obj.emailAddress,
					value: obj.emailAddress,
					id: index + 1,
				}));
				return engineers_options;
			}
			return [];
		} catch (error) {
			console.error("Error fetching engineers:", error);
			return [];
		}
	};

	useEffect(() => {
		let sortOptions;
		const columnFilterPayload = reportListColumnFilters?.map((element) => {
			let singleQuery = {
				key: element.id,
				value: element?.value,
			};
			return singleQuery;
		});
		if (reportListSort.length > 0) {
			sortOptions = reportListSort.map((x) => ({
				SortField: x.id === "WcnNumber" ? "WCN" : x.id,
				IsAscending: !x.desc,
			}));
			getAllReportsByYear(
				selectedYearOption[0],
				null,
				null,
				reportListPagination.pageIndex,
				reportListPagination.pageSize,
				sortOptions[0]
			);
		} else {
			if (
				reportListGlobalFilter !== "" &&
				previousGlobalFilter !== reportListGlobalFilter
			) {
				setPreviousGlobalFilter(reportListGlobalFilter);
				setReportListPagination({
					...reportListPagination,
					pageIndex: 0,
				});
				getAllReportsByYear(
					selectedYearOption[0],
					columnFilterPayload,
					reportListGlobalFilter,
					0,
					reportListPagination.pageSize,
					sortOptions
				);
			} else {
				getAllReportsByYear(
					selectedYearOption[0],
					columnFilterPayload,
					reportListGlobalFilter,
					reportListPagination.pageIndex,
					reportListPagination.pageSize,
					sortOptions
				);
			}
		}
	}, [
		selectedYearOption,
		reportListPagination.pageSize,
		reportListPagination.pageIndex,
		reportListGlobalFilter,
		reportListColumnFilters,
		reportListSort,
	]);

	useEffect(() => {
		setDisableBtn(false);
	}, [isImportReportModalOpen]);

	const applyColumnFilter = useCallback((columnId, value) => {
		setReportListPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));

		const isCleared =
			value == null ||
			value === "" ||
			(Array.isArray(value) && value.length === 0);

		const filters = [...reportListColumnFilters];

		const index = filters.findIndex(
			(f) => f.id === columnId
		);

		if (isCleared) {
			if (index >= 0) {
				filters.splice(index, 1);
			}
		} else {
			if (index >= 0) {
				filters[index] = {
					id: columnId,
					value,
				};
			} else {
				filters.push({
					id: columnId,
					value,
				});
			}
		}

		setReportListColumnFilters(filters);
	}, [reportListColumnFilters]);

	const reportListColumns = useMemo(() => {
		return [
			...REPORTS_TABLE_COLUMNS.map((column) => {
				if (!TEXT_FILTER_COLUMNS.includes(column.accessorKey)) {
					return column;
				}

				return {
					...column,
					Filter: () => (
						<ColumnFilterTextField
							columnId={column.accessorKey}
							applyColumnFilter={applyColumnFilter}
							applyFilters={applyFilters}
							initialValue={
								reportListColumnFilters.find(
									(f) => f.id === column.accessorKey
								)?.value ?? ""
							}
							isApplied={
								reportListColumnFilters.some(
									(f) => f.id === column.accessorKey
								)
							}
							ref={(instance) => {
								if (instance) {
									textFilterRefs.current[column.accessorKey] = instance;
								} else {
									delete textFilterRefs.current[column.accessorKey];
								}
							}}
						/>
					),
				};
			}),
			{
				accessorKey: "ImoVesselTypeName",
				header: "IMO Vessel Type",
				filterVariant: "custom",

				Cell: ({ cell }) => (
					<HighlightedCell
						value={cell.getValue()}
						columnId="ImoVesselTypeName"
						reportListColumnFilters={
							reportListColumnFilters
						}
						user={user}
					/>
				),

				Filter: () => (
					<MultiSelectFilter
						columnId="ImoVesselTypeName"
						options={imoVesselTypes}
						placeholder="Filter IMO Vessel Type"
						columnWidth='250px'
						getFilterValue={getFilterValue}
						updateFilterValue={updateFilterValue}
					/>
				),
			},
			{
				accessorKey: "MrvVesselTypeName",
				header: "MRV Vessel Type",

				Cell: ({ cell }) => (
					<HighlightedCell
						value={cell.getValue()}
						columnId="MrvVesselTypeName"
						reportListColumnFilters={
							reportListColumnFilters
						}
						user={user}
					/>
				),

				Filter: () => (
					<MultiSelectFilter
						columnId="MrvVesselTypeName"
						options={mrvVesselTypes}
						placeholder="Filter MRV Vessel Type"
						columnWidth='250px'
						getFilterValue={getFilterValue}
						updateFilterValue={updateFilterValue}
					/>
				),
			},
			{
				accessorKey: "Regulations",
				header: "Regulations",
				filterVariant: "custom",

				Cell: ({ cell }) => (
					<HighlightedCell
						value={cell.getValue()}
						columnId="Regulations"
						reportListColumnFilters={
							reportListColumnFilters
						}
						user={user}
					/>
				),

				Filter: () => (
					<MultiSelectFilter
						columnId="Regulations"
						options={reportTypes}
						placeholder="Filter Regulations"
						columnWidth='150px'
						getFilterValue={getFilterValue}
						updateFilterValue={updateFilterValue}
					/>
				),
			},
			{
				accessorKey: "ReportStatus",
				header: "Status",

				Cell: ({ cell }) => (
					<HighlightedCell
						value={cell.getValue()}
						columnId="ReportStatus"
						reportListColumnFilters={
							reportListColumnFilters
						}
						user={user}
					/>
				),

				Filter: () => (
					<MultiSelectFilter
						columnId="ReportStatus"
						options={reportStatuses}
						placeholder="Filter Status"
						columnWidth='250px'
						getFilterValue={getFilterValue}
						updateFilterValue={updateFilterValue}
					/>
				),
			},
			{
				accessorKey: "Reviewer",
				header: "Reviewer",

				Cell: ({ cell }) => (
					<HighlightedCell
						value={cell.getValue()}
						columnId="Reviewer"
						reportListColumnFilters={
							reportListColumnFilters
						}
						user={user}
					/>
				),

				Filter: () => (
					<MultiSelectFilter
						columnId="Reviewer"
						options={[
							...(user?.roles?.some(
								(role) =>
									role === "ER_REVIEWER" || role === "ER_SUPERADMIN"
							) ? ["@Me"] : []),
							"@Unassigned",
							...engineersOptions
						]}
						placeholder="Filter by Reviewer"
						columnWidth='300px'
						getFilterValue={getFilterValue}
						updateFilterValue={updateFilterValue}
					/>
				),
			},
			{
				accessorKey: "Verifier",
				header: "Verifier",

				Cell: ({ cell }) => (
					<HighlightedCell
						value={cell.getValue()}
						columnId="Verifier"
						reportListColumnFilters={
							reportListColumnFilters
						}
						user={user}
					/>
				),

				Filter: () => (
					<MultiSelectFilter
						columnId="Verifier"
						options={[
							...(user?.roles?.some(
								(role) =>
									role === "ER_VERIFIER" || role === "ER_SUPERADMIN"
							) ? ["@Me"] : []),
							"@Unassigned",
							...engineersOptions
						]}
						placeholder="Filter by Verifier"
						columnWidth='300px'
						getFilterValue={getFilterValue}
						updateFilterValue={updateFilterValue}
					/>
				),
			}
		]
	}, [reportTypes, reportStatuses, engineersOptions, imoVesselTypes, mrvVesselTypes, user, reportListColumnFilters, pendingReportListColumnFilters]);

	const handleReportListTableRowClick = (row, column) => {
		column.id !== "actionBtnName" &&
			navigate(`/reports/${row.original.ReportMstId}`);
	};

	// const handleSelectAll = (event) => {
	// 	setSelectAll(event.target.checked);
	// };

	const getButtonType = (rowData) => {
		if (rowData.ShowClaimButton) return "Claim";
		else if (rowData.ShowReassignButton) return "Re-Assign";
		else if (rowData.ShowUnclaimButton) return "Un-Claim";
		else if (rowData.ShowAssignButton) return "Assign";
	};

	const handleModel = (btnName, mstId) => {
		if (selectedEngineer === !"") {
			setdisableAssignBtn(false);
		} else {
			setdisableAssignBtn(true);
		}

		setActionBtnName(btnName);
		setselectedReport(mstId);
		if (btnName.includes("Assign")) {
			setShowModel(true);
		} else {
			assignEngineer(user ? user?.email : null, btnName, mstId);
		}
	};

	const getFilterValue = useCallback(
		(columnId) => {
			const filter = pendingReportListColumnFilters.find(
				(f) => f.id === columnId
			);

			return filter?.value || [];
		},
		[pendingReportListColumnFilters]
	);

const updateFilterValue = useCallback(
	(columnId, value) => {

		const isCleared =
			(Array.isArray(value) && value.length === 0) ||
			value === "" ||
			value == null;

		// update pending filters
		setPendingReportListColumnFilters((prev) => {

			const others = prev.filter(
				(f) => f.id !== columnId
			);

			return !isCleared
				? [
						...others,
						{
							id: columnId,
							value,
						},
				  ]
				: others;
		});

		// handle applied filters separately
		setReportListColumnFilters((prevApplied) => {

			const appliedFilter = prevApplied.find(
				(f) => f.id === columnId
			);

			const wasApplied =
				appliedFilter &&
				(
					(Array.isArray(appliedFilter.value) &&
						appliedFilter.value.length > 0) ||
					(!Array.isArray(appliedFilter.value) &&
						appliedFilter.value !== "" &&
						appliedFilter.value != null)
				);

			// only auto-clear if previously applied
			if (isCleared && wasApplied) {

				setReportListPagination((prev) => ({
					...prev,
					pageIndex: 0,
				}));

				return prevApplied.filter(
					(f) => f.id !== columnId
				);
			}

			return prevApplied;
		});
	},
	[]
);

	const getAllEngineers = async (input) => {
		try {
			const response = await await accessAPIForResponse(
				"GetReviewEngineers",
				null,
				[input]
			);
			if (response) {
				const res = response.map((engineer) => engineer.emailAddress);
				setEngineersOptions(res);
			} else {
				setEngineersOptions([]);
			}
		} catch (error) {
			console.error("Error fetching engineers:", error);
			setEngineersOptions([]);
		}
	};

	const getVesselTypes = async () => {
		try {
			const imoVesselTypeResponse = await axiosHttp.get(
			"api/MasterData/GetImoVesselType"
		);

			const mrvVesselTypeResponse = await axiosHttp.get(
			"api/MasterData/GetMrvVesselType"
		);

			if (imoVesselTypeResponse && mrvVesselTypeResponse) {

				setImoVesselTypes(
					imoVesselTypeResponse.data.map((item) => item.Value)
				);

				setMrvVesselTypes(
					mrvVesselTypeResponse.data.map((item) => item.Value)
				);
			} else {
				setImoVesselTypes([]);
				setMrvVesselTypes([]);
			}
		} catch (error) {
			console.error("Error fetching vessel types:", error);
			setImoVesselTypes([]);
			setMrvVesselTypes([]);
		}
	};

	useEffect(() => {
		getVesselTypes();
		getAllEngineers("");
	}, []);
	
	const applyFilters = () => {
		setReportListPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));

		const textFilters = Object.entries(textFilterRefs.current)
			.map(([columnId, ref]) => ({
				id: columnId,
				value: ref.getValue(),
			}))
			.filter(
				(filter) =>
					filter.value != null &&
					String(filter.value).trim() !== ""
			);

		const filters = [
			...textFilters,
			...pendingReportListColumnFilters,
		];

		setReportListColumnFilters(filters);

		const globalValue =
			globalSearchRef.current?.getValue() ?? "";

		setSearchInput(globalValue);
		setReportListGlobalFilter(globalValue);
	};

	const handleColumnFilterChange = (updater) => {
		setPendingReportListColumnFilters((prev) => {

			const updated =
				typeof updater === "function"
					? updater(prev)
					: updater;

			// detect removed filters
			const removedFilters = prev.filter(
				(oldFilter) =>
					!updated.some(
						(newFilter) =>
							newFilter.id === oldFilter.id
					)
			);

			// only consider filters that were actually applied
			const removedAppliedFilters =
				removedFilters.filter((removedFilter) =>
					reportListColumnFilters.some(
						(appliedFilter) =>
							appliedFilter.id === removedFilter.id
					)
				);

			// auto apply ONLY if applied filter removed
			if (removedAppliedFilters.length > 0) {

				setReportListPagination((prev) => ({
					...prev,
					pageIndex: 0,
				}));

				setReportListColumnFilters((prevApplied) =>
					prevApplied.filter(
						(appliedFilter) =>
							updated.some(
								(updatedFilter) =>
									updatedFilter.id === appliedFilter.id
							)
					)
				);
			}

			return updated;
		});
	};

	const clearAllFilters = useCallback(() => {
		setPendingReportListColumnFilters([]);
		setReportListColumnFilters([]);
		setReportListPagination((prev) => ({ ...prev, pageIndex: 0 }));
		setSearchInput('');
		setReportListGlobalFilter('');
		globalSearchRef.current?.clear();
		Object.values(textFilterRefs.current).forEach((ref) => {
    ref.clear();
});
	}, []);

	const applyGlobalSearch = useCallback((value) => {

    setSearchInput(value);

    setReportListPagination(prev => ({
        ...prev,
        pageIndex: 0,
    }));

    setReportListGlobalFilter(value);

}, []);
	
	const table = useMaterialReactTable(
		{
			columns: [
				...reportListColumns,
				{
					accessorKey: "download",
					header: " ",
					enableSorting: false,
					enableColumnActions: false,
					enableColumnFilters: false,
					enableCellActions: true,
					Filter: () => null,
					size: 50,
					Cell: ({ row: { original, depth } }) => {
						const handleDownloadClick = async (event) => {
							event.stopPropagation();
							setPageLoader(true);
							await accessAPIForResponse(
								"DownloadReportDocuments",
								null,
								[original.ReportMstId]
							)
								.then((response) => {
									setPageLoader(false);
									const url = window.URL.createObjectURL(
										new Blob([response], {
											type: response?.type,
										})
									);
									const link = document.createElement("a");
									link.href = url;
									link.setAttribute(
										"download",
										"Report documents.zip"
									);
									document.body.appendChild(link);
									link.click();
									emitToasterMsg(
										"success",
										"Report downloaded successfully."
									);
								})
								.catch((error) => {
									setPageLoader(false);
									console.error(error);
								});
						};
						return (
							<div className="text-left">
								{depth > 0 && (
									<Button
										outline={true}
										isIconOnly
										className="mr-1"
										variant="tertiary"
										disabled={!original.DocumentsAvailable}
										onClick={handleDownloadClick}
									>
										<Icon
											icon={ICONS.DOWNLOAD}
											// className={`d-flex ${!original.DocumentsAvailable ? 'hide-icon-reports-page-download' : ''}`}
											className="d-flex"
											disabled={
												!original.DocumentsAvailable
											}
											outline={true}
											size="24px"
										/>
									</Button>
								)}
							</div>
						);
					},
				},
				{
					accessorKey: "actionBtnName",
					accessorFn: (row) =>
						row.actionBtnName ? (
							<Box
								sx={{
									display: "flex",
									gap: "1rem",
									width: "120px",
								}}
							>
								<Tooltip title="Manage">
									<Button
										onClick={() => {
											setSelectedEngineer(row.Reviewer);
											handleModel(
												row.actionBtnName,
												row.ReportMstId
											);
										}}
									>
										{row.actionBtnName}
									</Button>
								</Tooltip>
							</Box>
						) : row.Reviewer !== "" &&
						  userHasClaim(ClaimType.CLAIM_REPORT) 
						  ? (
							<p className="">
								Claimed By <br />
								{row.Reviewer}
							</p>
						) : (
							""
						),
					header: "",
					enableColumnActions: false,
					enableColumnFilter: false,
				},
			],
			data: reportedData,
			// enableRowSelection: true,
			enableStickyHeader: true,
			rowCount,
			enableExpandAll: true,
			enableExpanding: true,
			manualPagination: true,
			enableBatchRowSelection: true,
			manualFiltering: true,
			manualSorting: true,
			enableSorting: false,
			filterFromLeafRows: true,
			enableTopToolbar: true,
			enableHiding: false,
			getSubRows: (row) => row.childReports,
			paginateExpandedRows: true,
			onPaginationChange: setReportListPagination,
			onGlobalFilterChange: (value) => {
				setSearchInput(value ?? ''); // keep input in sync
			},
			onSortingChange: setReportListSort,
			onColumnFiltersChange: handleColumnFilterChange,
			getRowId: (row) => row.ReportId,
			onRowSelectionChange: setReportListRowSelection,
			state: {
				rowSelection: reportListRowSelection,
				// isLoading: loading,
				pagination: reportListPagination,
				globalFilter: searchInput,
				columnFilters: reportListColumnFilters,
				sorting: reportListSort,
				showSkeletons: loading,
			},
			initialState: {
				density: "compact",
				expanded: true,
				columnOrder: [
					"download",
					"ReportId",
					"ImoNumber",
					"VesselName",
					"WcnNumber",
					"CompanyName",
					"ImoVesselTypeName",
					"MrvVesselTypeName",
					"Flag",
					"Regulations",
					"ReportStatus",
					"actionBtnName",
					"ModifiedDate",
					"ReportStartDate",
					"ReportEndDate",
					"SubmittedUserName",
					"correction",
					"ApprovedUserName",
					"VerifiedUserName",
					"AisStatus",
					"RevisionNumber",
				],
			},
			muiSkeletonProps: {
				animation: "wave",
				width: 100,
			},
			muiTableBodyRowProps: ({ row }) => ({
				// onClick: () => handleReportListTableRowClick(row),
				sx: {
					cursor: "pointer",
				},
			}),
			muiTableBodyCellProps: ({ row, row: { original }, column }) => ({
				component: CustomTableRow,
				ReportMstId: original.ReportMstId,
				column: column,
				//conditionally style pinned columns
				sx: {
					fontWeight: row.original.IsParentReport ? "bold" : "null",
				},
				onClick: () => handleReportListTableRowClick(row, column),
			}),
			muiPaginationProps: {
				rowsPerPageOptions: [25, 50, 75, 100],
				showFirstButton: true,
				showLastButton: true,
			},
			muiTableContainerProps: {
				sx: {
					maxHeight: "calc(100vh - 379px)",
				},
			},
			renderToolbarInternalActions: ({ table }) => (
				<>
					<Collapse
						in={showSearch}
						timeout="auto"
						unmountOnExit
					>
						<GlobalFilterTextField
							searchInput={searchInput}
							applyGlobalSearch={applyGlobalSearch}
							autoFocus={showSearch}
							ref={globalSearchRef}
						/>
					</Collapse>

					<IconButton
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => setShowSearch((prev) => !prev)}
					>
						{showSearch ? <SearchOff /> : <SearchIcon />}
					</IconButton>
					<MRT_ToggleFiltersButton
						id="reports_view_global_filter"
						table={table}
					/>
					<Button
						className="ml-2"
						onClick={() => {
							applyFilters();
						}}
					>
						Apply Filters
					</Button>
					<Button
						className="ml-2"
						variant="secondary"
						onClick={() => {
							clearAllFilters();
						}}
					>
						Clear
					</Button>
				</>
			),
			muiTableHeadCellProps: ({ column }) => ({
				id: `${column.columnDef.accessorKey}`,
				sx: {
					color: "rgb(255 255 255)",
					"& .MuiButtonBase-root": {
						color: "white !important",
						"& .MuiTableSortLabel-icon": {
							color: "white !important",
						},
					},
				},
			}),
			muiFilterTextFieldProps:  ({ column }) => ({
				sx: {
					"& .MuiInput-root": {
						color: "white !important",
					},
					"& .MuiButtonBase-root": {
						color: "white !important",
					},
					"& .MuiInputBase-root::after": {
						transform: "none",
						borderBottom: "2px solid white",
					},
				},
			}),
			muiColumnActionsButtonProps: {
				sx: {
					color: "white",
				},
			},
			// muiSelectAllCheckboxProps: {
			// 	title: selectAll ? "Toggle deselect all" : "Toggle select all",
			// 	onClick: (e) => handleSelectAll(e),
			// 	sx: {
			// 		color: "white",
			// 	},
			// },
			muiTableHeadRowProps: {
				sx: {
					backgroundColor: "#042a4c !important",

					"& .MuiTableCell-head::before": {
						backgroundColor: "#042a4c !important",
					},
				},
			},
			muiTablePaperProps: {
				sx: {
					boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
				},
			},
		},
		[]
	);

	const onReportYearChange = (selected) => {
		setSelectedYearOption(selected);
		let selectedYearOption = selected[0] || getReportingYear;
		localStorage.setItem("selectedYear", selectedYearOption);
	};

	const onWcnChange = (selected) => {
		setSelectedWcn(selected);
		sessionStorage.setItem("wcn", selected);
		window.location.reload(true);
	};

	const getAllReportsByYear = async (
		selectedReportYear,
		columnFilterPayload,
		searchText = "",
		pageIndex = 0,
		pageSize = 25,
		sortOptions = {
			sortField: "",
			isAscending: true,
		}
	) => {
		let filtersArray = columnFilterPayload?.map((filter) => ({
			key: filter.key === "WcnNumber" ? "WCN" : filter.key,
			value: Array.isArray(filter?.value) ? filter?.value?.map((item) => {
				if (item === "@Me") {
					return user?.email;
				}

				if (item === "@Unassigned") {
					return "";
				}

				return item;
			}) : filter.value,
		}));
		let requestPayload = {
			globalFilter: searchText,
			filters: filtersArray,
			pagination: {
				page: pageIndex + 1,
				pageSize: pageSize,
			},
			sortOption: sortOptions,
			reportYear: selectedReportYear,
		};
		setLoading(true);
		await accessAPIForResponse("GetReportList", requestPayload).then(
			(response) => {
				if (response?.Data) {
					groupChildReportsByParentId(response?.Data);
					setRowCount(response?.Pagination?.TotalItems);
					setReportListRowSelection({});
				}
			}
		);
		setLoading(false);
	};

	const groupChildReportsByParentId = (reports) => {
		const nestedReportsData = {};
		reports.forEach((report) => {
			if (report.IsParentReport) {
				nestedReportsData[`id:${report.ReportMstId}`] = {
					...report,
					childReports: [],
				};
			} else {
				const childReport = reportedData.find(
					(item) => item.ReportMstId === report.ParentReportMstId
				);
				if (nestedReportsData[`id:${report.ParentReportMstId}`]) {
					report.actionBtnName = getButtonType(report);
					nestedReportsData[
						`id:${report.ParentReportMstId}`
					].childReports.push(report);
				} else if (childReport) {
					report.actionBtnName = getButtonType(report);
					nestedReportsData[`id:${report.ParentReportMstId}`] = {
						...childReport,
						childReports: [],
					};
					nestedReportsData[
						`id:${report.ParentReportMstId}`
					].childReports.push(report);
				}
			}
		});
		const nestedReportsList = Object?.values(nestedReportsData);
		setReportedData(nestedReportsList);
	};

	const toggleImportReport = (needRefetch = false) => {
		setIsImportReportModalOpen(!isImportReportModalOpen);
		if (typeof needRefetch == "boolean" && needRefetch) {
			getAllReportsByYear(selectedYearOption[0]);
		}
	};

	const handleExportReportList = async () => {
		setExportReportEnabled(false);
		await accessAPIForResponse("ExportReportsList", null, [
			selectedYearOption[0],
		]).then((response) => {
			if (response) {
				const contentType = response?.type;
				if (
					contentType ===
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				) {
					const blob = new Blob([response]);
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute("download", "Report-List-Export.xlsx");
					document.body.appendChild(link);
					link.click();
					emitToasterMsg(
						"success",
						"Report-List-Export.xlsx",
						"Exported successfully"
					);
					document.body.removeChild(link);
				} else {
					emitToasterMsg(
						"error",
						"Export All Report-List",
						"Unexpected content type received"
					);
				}
				setExportReportEnabled(true);
			}
		});
	};

	const breadcrumb = useMemo(
		() => [
			{
				label: "Annual Emission Verification",
				url: "",
				navId: "annual_emission_Verification_navId",
			},
			{ label: "Reports", url: "", navId: "reports_navId" },
		],
		[]
	);

	const toggleClose = () => {
		setShowModel(false);
	};

	const assignEngineer = async (engineer, btnName, mstId) => {
		const requestPayLoad = btnName.includes("Claim")
			? {
					reportMstId: mstId,
					userEmail: engineer,
			  }
			: {
					reportMstId: selectedReport,
					assigneeUserEmail: engineer,
			  };

		await accessAPIForResponse(
			btnName === "Claim"
				? "ClaimReport"
				: btnName === "Un-Claim"
				? "UnclaimReport"
				: "AssignReport",
			requestPayLoad,
			[]
		)
			.then((response) => {
				if (response && response.IsSuccess) {
					setdisableAssignBtn(true);
					setShowModel(false);
					setReportListGlobalFilter(
						reportListGlobalFilter ? reportListGlobalFilter : ""
					);
					getAllReportsByYear(
						selectedYearOption[0],
						null,
						reportListGlobalFilter ? reportListGlobalFilter : null,
						reportListPagination.pageIndex,
						reportListPagination.pageSize,
						{
							sortField: "",
							isAscending: true,
						}
					);
				} else console.error(response.ErrorMessage);
			})
			.catch((error) => {
				console.error(error);
			});
	};

	return (
		<>
			<div className="report-list-container p-4">
				{pageLoader && <Loader />}
				{showbreadCrumb && <BreadCrumb items={breadcrumb} />}
				<div className="d-flex flex-row justify-content-between px-3">
					<div className="reporting-yr-wrapper d-flex">
						<Label className="mr-3" name="Reporting Year" bold />
						<div className="drop-down-container">
							<GenericDropdown
								options={options}
								selected={selectedYearOption}
								handleChange={onReportYearChange}
								placeholder={selectedYearOption[0]}
							/>
						</div>
					</div>

					<div className="d-flex flex-row justify-content-between">
						{/* This is for future reference */}
						{/* <Button
							variant="secondary"
							disabled={false}
							startIcon={<Icon icon="ios_share" />}
							className="mr-2"
						>
							Export Report Data
						</Button> */}

						{showbreadCrumb && (
							<Button
								className="justify-content-end mr-2"
								startIcon={<Icon icon={"exit_to_app"} />}
								disabled={isImportReportModalOpen}
								onClick={toggleImportReport}
							>
								Import Report(s)
							</Button>
						)}
						<Button
							variant="secondary"
							disabled={!exportReportEnabled}
							startIcon={<Icon icon="ios_share" />}
							onClick={handleExportReportList}
						>
							{exportReportEnabled
								? "Export Report(s)"
								: "Please wait..."}
						</Button>
					</div>
				</div>

				<div className="mt-4 report-list-table">
					<MaterialReactTable table={table} />
				</div>
			</div>

			{isImportReportModalOpen && (
				<AbsModal
					isOpen={isImportReportModalOpen}
					title="Import Report(s) "
					onClose={setIsImportReportModalOpen}
					close={() => toggleImportReport(false)}
				>
					<ImportReport
						cancelImportReport={toggleImportReport}
						reportingYear={selectedYearOption}
						disableBtn={disableBtn}
						setDisableBtn={setDisableBtn}
						onImportSuccess={() => getAllReportsByYear(selectedYearOption[0])}
					/>
				</AbsModal>
			)}

			{showModel && (
				<div className="assign-modal">
					<AbsModal
						isOpen={showModel}
						title={"Assignment"}
						onClose={setShowModel}
						close={toggleClose}
					>
						<div className="ml-3">
							<h4 className="blod">Assignment</h4>
							<hr />
							<p>Please select an Engineer</p>
							<hr />

							{/* <GenericDropdown
								options={engineers}
								selected={selectedEngineer}
								handleChange={(selected) => {
									selected &&
										setSelectedEngineer(selected[0]);
								}}
								placeholder={
									selectedEngineer || "Select an Engineer"
								}
							/> */}

							<AutocompleteDropdown
								className="inputHeight"
								id="search-vessel-id"
								isClearable
								loadOptions={async (inputText) => {
									const options = await GetEngineersList(
										inputText
									);
									return options;
								}}
								placeholder={
									selectedEngineer || "Select an Engineer"
								}
								autoFocus={false}
								onChange={handleSelect}
								customStyles={{
									menuList: (provided) => ({
										...provided,
									}),
								}}
							/>

							<div className="d-flex justify-content-end mr-3 assignment-model-btns">
								<Button
									className="mr-3"
									id="assign-claim-btn-report-list"
									onClick={() => {
										assignEngineer(
											selectedEngineer,
											actionBtnName
										);
									}}
									disabled={disableAssignBtn}
								>
									Assign/Reassign
								</Button>
								<Button
									id="cancel-btn-report-list"
									variant="secondary"
									onClick={() => {
										toggleClose();
									}}
								>
									Cancel
								</Button>
							</div>
						</div>
					</AbsModal>
				</div>
			)}
		</>
	);
};

export default ReportsPage;